import { Box, Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertBuildingAmenityLot,
  convertBuildingStandard,
  convertFencingExtent,
  convertFrontFencing,
  convertLandOccupationType,
  convertPreservationState,
} from '../../../api/characteristics/enumerations';
import {
  LotEnterprise,
  SectionStatus,
} from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { AnswerTag, AnswerTagsBox, StyledDivider } from '../styles';

interface LotEnterpriseSectionProps {
  formData: LotEnterprise;
  sectionStatus?: SectionStatus;
}

export const LotEnterpriseSection = memo(function LotEnterpriseSection({
  formData,
  sectionStatus,
}: LotEnterpriseSectionProps) {
  return (
    <Box>
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <SectionStatusBadge status={sectionStatus} />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="enterprise-dados-gerais"
          >
            Dados Gerais
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Padrão do Empreendimento
              </Typography>
              <Typography>
                {formData.general_info.enterprise_standard
                  ? convertBuildingStandard(
                      formData.general_info.enterprise_standard
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Estado de Conservação
              </Typography>
              <Typography>
                {formData.general_info.preservation_state
                  ? convertPreservationState(
                      formData.general_info.preservation_state
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Controle de Acesso
              </Typography>
              <Typography>
                {formData.general_info.access_control == null
                  ? '-'
                  : String(
                      formData.general_info.access_control ? 'Sim' : 'Não'
                    )}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Nº Total de Unidades
              </Typography>
              <Typography>
                {formData.general_info.total_units ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Nº de Und. de Tipologia
              </Typography>
              <Typography>
                {formData.general_info.number_of_unit_typologies ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Fração Ideal
              </Typography>
              <Typography>
                {formData.general_info.ideal_fraction ?? '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize={18} fontWeight={600} id="enterprise-terreno">
            Terreno
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cercamento
              </Typography>
              <Typography>
                {formData.terrain.fencing
                  ? convertFencingExtent(formData.terrain.fencing)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Tipo de Cercamento Frente
              </Typography>
              <Typography>
                {formData.terrain.front_fence
                  ? convertFrontFencing(formData.terrain.front_fence)
                  : '-'}
              </Typography>
            </Grid>
            {formData.terrain.front_fence_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Tipo de Cercamento Frente - Outros
                </Typography>
                <Typography>{formData.terrain.front_fence_other}</Typography>
              </Grid>
            )}
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Tipo de Cercamento Lateral/Fundo
              </Typography>
              <Typography>
                {formData.terrain.side_back_fence
                  ? convertFrontFencing(formData.terrain.side_back_fence)
                  : '-'}
              </Typography>
            </Grid>
            {formData.terrain.side_back_fence_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Tipo de Cercamento Lateral/Fundo - Outros
                </Typography>
                <Typography>
                  {formData.terrain.side_back_fence_other}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography fontWeight={600}>Ocupação do terreno</Typography>
        </Grid>
        <Grid item xs={12}>
          <AnswerTagsBox>
            {(formData.terrain.land_occupation ?? []).length > 0 ? (
              (formData.terrain.land_occupation ?? []).map((v) => (
                <AnswerTag key={v}>{convertLandOccupationType(v)}</AnswerTag>
              ))
            ) : (
              <Typography>-</Typography>
            )}
            {formData.terrain.land_occupation_other && (
              <AnswerTag>{formData.terrain.land_occupation_other}</AnswerTag>
            )}
          </AnswerTagsBox>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="enterprise-equipamentos"
          >
            Equipamentos
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <AnswerTagsBox>
            {(formData.equipments.items ?? []).length > 0 ? (
              (formData.equipments.items ?? []).map((v) => (
                <AnswerTag key={v}>{convertBuildingAmenityLot(v)}</AnswerTag>
              ))
            ) : (
              <Typography>-</Typography>
            )}
            {formData.equipments.other_equipments && (
              <AnswerTag>{formData.equipments.other_equipments}</AnswerTag>
            )}
          </AnswerTagsBox>
          {formData.equipments.leisure_halls_quantity != null && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  N° Salas de Lazer
                </Typography>
                <Typography>
                  {formData.equipments.leisure_halls_quantity}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="enterprise-dados-administrativos"
          >
            Dados Administrativos
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Taxa de Condomínio
              </Typography>
              <Typography>
                {formData.general_info.condominium_fee ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Administradora do Condomínio
              </Typography>
              <Typography>
                {formData.general_info.condominium_manager ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Telefone Administradora
              </Typography>
              <Typography>
                {formData.general_info.condominium_phone ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                E-mail Administradora
              </Typography>
              <Typography>
                {formData.general_info.condominium_email ?? '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
});
